import { useCallback, useRef, useState } from "react";
import { ApiError, type ApiClient } from "../api/client";
import type { AppSession, DeviceStatus } from "../api/types";
import { LIVE_CLOSE } from "../app/useLive";
import { t } from "../i18n";
import { useToast } from "../ui/toasts";
import { AppHead, AppPage, ModeMenu, WhoButton, type ModeChoice } from "./AppScreens";
import { CourierApp } from "./courier/CourierApp";
import { openOwnersTill } from "./owner-till";
import { WaiterApp } from "./waiter/WaiterApp";

interface Props {
  api: ApiClient;
  status: DeviceStatus;
  session: AppSession;
  /** Errors that change what the app can do (unpaired, signed out, terms) move it to the right screen. */
  handleFatal: (error: unknown) => boolean;
  onSignOut: () => void;
  /** Ober ⇄ Koerier: the device is the other kind now; the same person stays signed in. */
  onModeSwitched: () => void;
}

/** What the screens of the signed-in app share: the server, who is signed in, messages. */
export interface AppEnv {
  api: ApiClient;
  status: DeviceStatus;
  session: AppSession;
  /** The green bar: the name on the left, Afmelden on the right (prototype ph-head). */
  head: React.ReactNode;
  report: (error: unknown) => void;
  toast: (message: string, tone?: "info" | "bad") => void;
  /** The live channel refused this device for good (unpaired, paused shop, terms). */
  onLiveRefused: (code: number) => void;
}

/**
 * The signed-in Tafo app: the waiter's screens or the courier's, never both at once (prototype: "Ober- en
 * koerierschermen blijven volledig gescheiden"). Whoever may use both switches with "Ober ⇄ Koerier" at
 * the top (Furkan, 09/10/2026).
 */
export function AppShell({ api, status, session, handleFatal, onSignOut, onModeSwitched }: Props) {
  const toast = useToast();
  /** Switching to the other app: what the old screens still hear back is not shown (see `leftWith`). */
  const leaving = useRef(false);
  const report = useCallback(
    (error: unknown) => {
      if (handleFatal(error)) return;
      if (leaving.current) return;
      toast(error instanceof ApiError ? error.message : t.errors.unexpected, "bad");
    },
    [handleFatal, toast],
  );
  const onLiveRefused = useCallback(
    (code: number) => {
      const reason =
        code === LIVE_CLOSE.notPaired
          ? new ApiError(401, "device_not_paired", "")
          : code === LIVE_CLOSE.signedOut
            ? new ApiError(401, "staff_pin_required", "")
            : code === LIVE_CLOSE.shopInactive
              ? new ApiError(403, "shop_inactive", "")
              : new ApiError(423, "terms_required", "");
      handleFatal(reason);
    },
    [handleFatal],
  );
  // who may use both apps switches at the top, without signing in again (Furkan, 09/10/2026)
  const both = session.staff.can.includes("orders.create") && session.staff.can.includes("courier.app");
  const [switching, setSwitching] = useState(false);
  /**
   * The status this app is being switched away from: from the tap until the app has reloaded (a new
   * status), the screens of the old role are gone, so none of them asks the server anything the device may
   * no longer do, and what they still hear back is not shown (Furkan's video for Apple, 10/10/2026:
   * "Enkel in de koerier-app" in red after Koerier → Ober; the server tells every screen the moment the
   * device changes, before the app itself hears the answer).
   */
  const [leftWith, setLeftWith] = useState<DeviceStatus | null>(null);
  const switchTo = async (role: "waiter" | "courier") => {
    if (switching || role === status.device.role) return;
    setSwitching(true);
    leaving.current = true;
    setLeftWith(status);
    try {
      await api.request("POST", "/device/app-mode", { body: { role } });
      toast(t.tafoApp.mode.now(t.tafoApp.appNames[role]));
      onModeSwitched();
    } catch (error) {
      // not switched: the same screens come back
      leaving.current = false;
      setLeftWith(null);
      report(error);
      setSwitching(false);
    }
  };
  /** "Kassa": only the owner, the full till of app.tafo.be on this phone (Furkan, 18/10/2026). */
  const owner = session.staff.role === "owner";
  const openTill = async () => {
    if (switching) return;
    setSwitching(true);
    toast(t.tafoApp.mode.tillOpening);
    try {
      await openOwnersTill(api);
    } catch (error) {
      report(error);
      setSwitching(false);
    }
  };
  /** The app on screen: the courier's, or else the waiter's (see the end). */
  const role: "waiter" | "courier" = status.device.role === "courier" ? "courier" : "waiter";
  const choices: ModeChoice[] = [
    ...(both ? (["waiter", "courier"] as const) : [role]),
    ...(owner ? (["till"] as const) : []),
  ];
  const head = (
    <AppHead
      left={
        choices.length > 1 && (
          <ModeMenu
            current={role}
            choices={choices}
            disabled={switching}
            onPick={(choice) => void (choice === "till" ? openTill() : switchTo(choice))}
          />
        )
      }
      right={<WhoButton name={session.staff.name} onSignOut={onSignOut} />}
    />
  );
  if (leftWith === status) return <AppPage head={head}>{null}</AppPage>;
  const env: AppEnv = { api, status, session, head, report, toast, onLiveRefused };
  return status.device.role === "courier" ? <CourierApp env={env} /> : <WaiterApp env={env} />;
}
